<template>
  <div class="slot-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item v-if="showTypeSection" title="槽口类型" name="type">
        <div class="slot-type-grid">
          <button
            type="button"
            class="slot-type-button"
            :class="{ active: modeValue === SketchSlotCommand.Straight }"
            :disabled="!canEditOptions"
            @click="handleModeChange(SketchSlotCommand.Straight)"
          >
            <SvgIcon :content="slotStraightIcon" alt="直槽口" />
          </button>
          <button
            type="button"
            class="slot-type-button"
            :class="{ active: modeValue === SketchSlotCommand.CenterStraight }"
            :disabled="!canEditOptions"
            @click="handleModeChange(SketchSlotCommand.CenterStraight)"
          >
            <SvgIcon :content="slotCenterStraightIcon" alt="中心点直槽口" />
          </button>
          <button
            type="button"
            class="slot-type-button"
            :class="{ active: modeValue === SketchSlotCommand.ThreePointArc }"
            :disabled="!canEditOptions"
            @click="handleModeChange(SketchSlotCommand.ThreePointArc)"
          >
            <SvgIcon :content="slotArc3PointIcon" alt="三点圆弧槽口" />
          </button>
          <button
            type="button"
            class="slot-type-button"
            :class="{ active: modeValue === SketchSlotCommand.CenterArc }"
            :disabled="!canEditOptions"
            @click="handleModeChange(SketchSlotCommand.CenterArc)"
          >
            <SvgIcon :content="slotArcCenterIcon" alt="中心点圆弧槽口" />
          </button>
        </div>
      </n-collapse-item>

      <n-collapse-item v-if="showDimensionsSection" title="添加尺寸" name="dimensions">
        <n-checkbox
          :checked="options.addDimensions"
          :disabled="!canEditOptions"
          @update:checked="handleAddDimensions"
        >
          添加尺寸
        </n-checkbox>
      </n-collapse-item>

      <n-collapse-item v-if="showLengthTypeSection" title="长度类型" name="lengthType">
        <div class="length-type-row">
          <button
            type="button"
            class="length-type-button"
            :class="{ active: options.lengthType === SlotLengthType.CenterCenter }"
            :disabled="!canEditOptions"
            @click="handleLengthTypeChange(SlotLengthType.CenterCenter)"
          >
            中心-中心
          </button>
          <button
            type="button"
            class="length-type-button"
            :class="{ active: options.lengthType === SlotLengthType.Overall }"
            :disabled="!canEditOptions"
            @click="handleLengthTypeChange(SlotLengthType.Overall)"
          >
            总长
          </button>
        </div>
      </n-collapse-item>

      <n-collapse-item title="选项" name="options">
        <n-checkbox
          :checked="constructionChecked"
          :disabled="!canEditConstruction"
          @update:checked="handleForConstruction"
        >
          作为构造线
        </n-checkbox>
      </n-collapse-item>

      <n-collapse-item title="参数" name="parameters">
        <div class="param-grid">
          <div class="param-row">
            <n-text depth="3" class="param-label">X</n-text>
            <n-input-number
              :value="center2d?.[0] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleCenterInput('x', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">Y</n-text>
            <n-input-number
              :value="center2d?.[1] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleCenterInput('y', value)"
            />
          </div>
          <div v-if="showArcFields" class="param-row">
            <n-text depth="3" class="param-label">R</n-text>
            <n-input-number
              :value="radius ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="handleRadiusInput"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">{{ lengthLabel }}</n-text>
            <n-input-number
              :value="displayLength ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="handleDisplayLengthInput"
            />
          </div>
          <div v-if="showArcFields" class="param-row">
            <n-text depth="3" class="param-label">角度</n-text>
            <n-input-number
              :value="angle ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="handleAngleInput"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">宽度</n-text>
            <n-input-number
              :value="width ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="handleWidthInput"
            />
          </div>
        </div>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="现有几何关系" name="relations">
        <SelectionListFrame
          :items="existingRelations"
          selection-behavior="ctrl-multiple"
          list-role="list"
          :visible-rows="4"
          context-menu-enabled
          :is-item-delete-enabled="isSketchRelationSnapshotDeleteEnabled"
          :show-delete-all-action="existingRelations.some(isSketchRelationSnapshotDeleteEnabled)"
          item-key-field="relationId"
          @delete-item="handleDeleteRelationSelection"
          @delete-all="handleDeleteAllRelations"
        >
          <template #item="{ item }">
            <SketchRelationDiagnosticText
              :item="item"
              :label="resolveRelationLabel(item)"
            />
          </template>
        </SelectionListFrame>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, onMounted } from 'vue';
import type { Subscription } from 'rxjs';
import {
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NInputNumber,
  NText,
} from 'naive-ui';
import type { PropertyManagerContext, SlotOptions, SlotParameters, SlotParameterUpdate } from '@/core';
import { SlotLengthType } from '@/core';
import * as THREE from 'three';
import { useViewer } from '@/hooks';
import { useGetSelectSlot } from '@/hooks';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import SketchRelationDiagnosticText from '../common/SketchRelationDiagnosticText.vue';
import {isSketchRelationSnapshotDeleteEnabled} from '@/utils/sketch/isSketchRelationSnapshotDeleteEnabled';

import {
  normalizeArcAngles,
  roundToEightDecimalPlaces,
  SketchSlotCommand,
  type SketchRelationSnapshot,
  isSketchCollectionObject,
  isSketchLineObject,
  isSketchSlotObject,
  type SketchCollectionObject,
  type SketchLineObject,
  SketchSlotObject,
  //------ 统一单位转换 开始------
  convertMmToM,
  convertMToMm,
  //------ 统一单位转换 结束------
} from '@puppycad/cad-core';
import { SketchDrawingTool } from '@puppycad/cad-core';
import SvgIcon from '@/components/common/SvgIcon.vue';
import slotStraightIcon from '@/assets/icon/slot_straight.svg?raw';
import slotCenterStraightIcon from '@/assets/icon/slot_center_straight.svg?raw';
import slotArc3PointIcon from '@/assets/icon/slot_arc_3point.svg?raw';
import slotArcCenterIcon from '@/assets/icon/slot_arc_center.svg?raw';

defineOptions({
  inheritAttrs: false,
});

interface SlotPropertySectionProps {
  /** 属性面板上下文，用于统一提交草图属性编辑。 */
  context?: PropertyManagerContext;
}

const props = defineProps<SlotPropertySectionProps>();

type SlotMode = SketchSlotCommand;


const defaultOptions: SlotOptions = {
  mode: SketchSlotCommand.Straight,
  addDimensions: true,
  forConstruction: false,
  lengthType: SlotLengthType.CenterCenter,
};

//------ 槽口属性迁移 开始------
const { getViewer } = useViewer();
const { getSelected: getSelectedSlot, onChanged: onSlotChanged } = useGetSelectSlot();

const localSlotOptions = ref<SlotOptions>({ ...defaultOptions });
const localSlotParameters = ref<SlotParameters | null>(null);
const isDrawingPreview = ref(false);
/** 构造线勾选状态。 */
const constructionChecked = ref(false);

/** 是否有槽口被选中。 */
const isSlotSelected = ref(false);
/** 当前选中槽口的现有关系快照列表。 */
const existingRelations = ref<SketchRelationSnapshot[]>([]);
const slotChangedSub = onSlotChanged().subscribe((slot) => {
  isSlotSelected.value = Boolean(slot && isSketchSlotObject(slot));
  isDrawingPreview.value = false;
  refreshExistingRelations(slot);
  syncSlotPanelState();
  syncConstructionCheckedState(slot);
});
let slotParametersSubscription: Subscription | null = null;
onMounted(() => {
  syncSlotPanelState();
  syncConstructionCheckedState(getSelectedSlot());
  const slotParameters$ = getViewer()?.observeSlotDrawingParameters?.();
  if (!slotParameters$) {
    return;
  }
  slotParametersSubscription = slotParameters$.subscribe((parameters) => {
    if (parameters) {
      isDrawingPreview.value = true;
      localSlotParameters.value = parameters;
      return;
    }
    isDrawingPreview.value = false;
    syncSlotPanelState();
    syncConstructionCheckedState(getSelectedSlot());
  });
});
onBeforeUnmount(() => {
  slotChangedSub.unsubscribe();
  slotParametersSubscription?.unsubscribe();
});
const canEditOptions = computed(() => Boolean(getViewer()));
const canEditParameters = computed(() => Boolean(isSlotSelected.value && getViewer()));
const canEditConstruction = computed(() => Boolean(getViewer()));
const showAdvancedSections = computed(() => isSlotSelected.value);
const options = computed(() => localSlotOptions.value);
const params = computed(() => localSlotParameters.value);
const isSlotToolActive = computed(() => {
  const currentTool = getViewer()?.getCurrentSketchDrawingTool?.() ?? null;
  return currentTool === SketchDrawingTool.Slot;
});
const modeValue = computed(() => {
  // 槽口绘制态下优先展示当前绘制选项，避免被已选实体参数覆盖导致按钮看起来“点不动”。
  if (isSlotToolActive.value) {
    return options.value.mode as SlotMode;
  }
  return (params.value?.mode ?? options.value.mode) as SlotMode;
});
//------ 统一单位转换 开始------
const center2d = computed(() => {
  const c = params.value?.center2d ?? null;
  if (!c) return null;
  return [
    roundToEightDecimalPlaces(convertMToMm(c[0])),
    roundToEightDecimalPlaces(convertMToMm(c[1])),
  ];
});
const isArcMode = computed(() => (
  modeValue.value === SketchSlotCommand.ThreePointArc
  || modeValue.value === SketchSlotCommand.CenterArc
));
const displayLength = computed(() => {
  if (!params.value) {
    return null;
  }
  let rawVal: number | null = null;
  if (!isArcMode.value && options.value.lengthType === SlotLengthType.Overall) {
    rawVal = params.value.overallLength ?? params.value.displayLength ?? params.value.centerLength ?? null;
  } else {
    rawVal = params.value.centerLength ?? params.value.displayLength ?? null;
  }
  if (rawVal === null) return null;
  return roundToEightDecimalPlaces(convertMToMm(rawVal));
});
const width = computed(() => {
  const w = params.value?.width ?? null;
  if (w === null) return null;
  return roundToEightDecimalPlaces(convertMToMm(w));
});
const radius = computed(() => {
  const r = params.value?.radius ?? null;
  if (r === null || r === undefined) return null;
  return roundToEightDecimalPlaces(convertMToMm(r));
});
//------ 统一单位转换 结束------
const angle = computed(() => params.value?.angle ?? null);
const showArcFields = computed(() => (
  modeValue.value === SketchSlotCommand.ThreePointArc
  || modeValue.value === SketchSlotCommand.CenterArc
));
const showLengthTypeSection = computed(() => !isArcMode.value);
const showDimensionsSection = computed(() => {
  return isSlotToolActive.value && !isSlotSelected.value;
});
const showTypeSection = computed(() => {
  return isSlotToolActive.value || !isSlotSelected.value;
});

/**
 * 从槽口线段集合中提取线对象。
 * @param collection 槽口线段集合。
 * @returns 线对象列表。
 */
function resolveCollectionLines(collection: SketchCollectionObject): SketchLineObject[] {
  return Array.from(collection.collectSet).filter((item): item is SketchLineObject => isSketchLineObject(item));
}

/**
 * 同步“作为构造线”勾选状态。
 * @param slot 当前选中对象。
 */
function syncConstructionCheckedState(slot: ReturnType<typeof getSelectedSlot>): void {
  if (slot && isSketchSlotObject(slot)) {
    constructionChecked.value = Boolean(slot.isConstruction);
    return;
  }
  // 槽口是线段组合，从第一条线段读取构造线状态。
  if (slot && isSketchCollectionObject(slot)) {
    constructionChecked.value = Boolean(resolveCollectionLines(slot)[0]?.isConstruction);
    return;
  }
  constructionChecked.value = Boolean(localSlotOptions.value.forConstruction);
}

const lengthLabel = computed(() => {
  if (isArcMode.value) {
    return '角度';
  }
  return options.value.lengthType === SlotLengthType.Overall ? '总长' : '中心距';
});
const defaultExpanded = ['type', 'dimensions', 'lengthType', 'options', 'parameters'];

function syncSlotPanelState(): void {
  const renderer = getViewer();
  localSlotOptions.value = renderer?.getSlotDrawingOptions?.() ?? { ...defaultOptions };
  localSlotParameters.value = renderer?.getSlotDrawingParameters?.() ?? null;
  if (!localSlotParameters.value) {
    syncSlotParametersFromSelectedSlot(getSelectedSlot());
  }
}

function syncSlotParametersFromSelectedSlot(slot: ReturnType<typeof getSelectedSlot>): void {
  if (!slot || !isSketchSlotObject(slot)) {
    localSlotParameters.value = null;
    return;
  }
  const geometryData = slot.geometryData as Record<string, unknown> | null;
  if (!geometryData) {
    localSlotParameters.value = null;
    return;
  }
  const toVector3 = (value: unknown): THREE.Vector3 | null => {
    if (!Array.isArray(value) || value.length < 3) {
      return null;
    }
    const [x, y, z] = value;
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      return null;
    }
    return new THREE.Vector3(Number(x), Number(y), Number(z));
  };
  const centerWorld = toVector3(geometryData.center);
  const startWorld = toVector3(geometryData.centerlineStart);
  const endWorld = toVector3(geometryData.centerlineEnd);
  if (!centerWorld || !startWorld || !endWorld) {
    localSlotParameters.value = null;
    return;
  }
  const origin = toVector3(geometryData.planeOrigin) ?? new THREE.Vector3();
  const xAxis = toVector3(geometryData.xAxis)?.normalize() ?? new THREE.Vector3(1, 0, 0);
  const yAxis = toVector3(geometryData.yAxis)?.normalize() ?? new THREE.Vector3(0, 1, 0);
  const toLocal2D = (worldPoint: THREE.Vector3): [number, number] => {
    const relative = worldPoint.clone().sub(origin);
    return [
      roundToEightDecimalPlaces(relative.dot(xAxis)),
      roundToEightDecimalPlaces(relative.dot(yAxis)),
    ];
  };
  localSlotParameters.value = {
    mode: (geometryData.mode as SlotOptions['mode']) ?? localSlotOptions.value.mode,
    center: centerWorld.toArray() as [number, number, number],
    center2d: toLocal2D(centerWorld),
    start: startWorld.toArray() as [number, number, number],
    start2d: toLocal2D(startWorld),
    end: endWorld.toArray() as [number, number, number],
    end2d: toLocal2D(endWorld),
    width: Number(geometryData.width ?? 0),
    centerLength: Number(geometryData.centerLength ?? geometryData.displayLength ?? 0),
    overallLength: Number(geometryData.overallLength ?? geometryData.displayLength ?? 0),
    displayLength: Number(geometryData.displayLength ?? geometryData.centerLength ?? 0),
    radius: geometryData.radius == null ? undefined : Number(geometryData.radius),
    angle: geometryData.angle == null ? undefined : Number(geometryData.angle),
  };
}

/**
 * 将槽口属性编辑统一交给 PropertyManagerContext 收口。
 * @param slot 被编辑的槽口对象。
 */
function finalizeSlotPropertyEdit(slot: ReturnType<typeof getSelectedSlot>): void {
  if (!slot) {
    return;
  }
  props.context?.finalizeSketchPropertyEdit?.(slot, {
    reason: 'property-edit',
    sourceObjectId: slot.uuid,
  });
}

function updateOptions(patch: Partial<SlotOptions>): void {
  const renderer = getViewer();
  const currentOptions = renderer?.getSlotDrawingOptions?.();
  if (!renderer || !currentOptions) {
    return;
  }
  const nextOptions = { ...currentOptions, ...patch };
  renderer.setSlotDrawingOptions?.(nextOptions);
  // 兼容旧实现：若未提供完整 options setter，至少保证模式可同步。
  if (!renderer.setSlotDrawingOptions) {
    renderer.setSlotDrawingMode?.(String(nextOptions.mode));
  }
  localSlotOptions.value = nextOptions;
  syncConstructionCheckedState(getSelectedSlot());
}

function handleModeChange(mode: SlotMode): void {
  const renderer = getViewer();
  if (renderer && renderer.getCurrentSketchDrawingTool?.() !== SketchDrawingTool.Slot) {
    renderer.startDrawing(SketchDrawingTool.Slot);
  }

  if (mode === SketchSlotCommand.ThreePointArc || mode === SketchSlotCommand.CenterArc) {
    updateOptions({ mode, lengthType: SlotLengthType.CenterCenter });
    return;
  }
  updateOptions({ mode });
}

function handleAddDimensions(value: boolean): void {
  updateOptions({ addDimensions: value });
}

function handleLengthTypeChange(value: SlotLengthType): void {
  if (isArcMode.value) {
    return;
  }
  updateOptions({ lengthType: value });
}

//------ 槽口现有几何关系 开始------

/**
 * 刷新当前选中槽口的现有关系列表。
 * @param slot 选中的槽口对象（null 表示无选中）。
 */
function refreshExistingRelations(slot: ReturnType<typeof getSelectedSlot>): void {
  if (!slot || !slot.constraints?.getRelationSnapshots) {
    existingRelations.value = [];
    return;
  }
  existingRelations.value = slot.constraints.getRelationSnapshots();
}

/**
 * 将关系快照转为显示标签。
 * @param item 列表项。
 * @returns 显示标签。
 */
function resolveRelationLabel(item: unknown): string {
  const snapshot = item as SketchRelationSnapshot | null;
  if (!snapshot) {
    return '';
  }
  return snapshot.baseDisplayName || snapshot.name || String(snapshot.relationType);
}

/**
 * 处理右键删除单个关系。
 * @param payload SelectionListFrame delete-item 回调参数。
 */
async function handleDeleteRelationSelection(payload: { item: unknown; index: number }): Promise<void> {
  const slot = getSelectedSlot();
  if (!slot) {
    return;
  }
  const snapshot = payload.item as SketchRelationSnapshot;
  if (!snapshot || !Number.isFinite(snapshot.id) || !isSketchRelationSnapshotDeleteEnabled(snapshot)) {
    return;
  }
  const removed = await props.context?.removeSketchRelation?.(slot, snapshot.id) ?? false;
  if (!removed) {
    return;
  }
  const viewer = getViewer();
  viewer?.removeObjectConstraintMarker?.(slot, snapshot.id);
  refreshExistingRelations(slot);
}

/**
 * 处理右键删除全部关系。
 */
async function handleDeleteAllRelations(): Promise<void> {
  const slot = getSelectedSlot();
  if (!slot) {
    return;
  }
  const viewer = getViewer();
  for (const snapshot of existingRelations.value.filter(isSketchRelationSnapshotDeleteEnabled)) {
    const removed = await props.context?.removeSketchRelation?.(slot, snapshot.id) ?? false;
    if (!removed) {
      return;
    }
    viewer?.removeObjectConstraintMarker?.(slot, snapshot.id);
  }
  refreshExistingRelations(slot);
}

//------ 槽口现有几何关系 结束------

function handleForConstruction(value: boolean): void {
  if (isSlotSelected.value) {
    const selectedSlot = getSelectedSlot();
    if (selectedSlot && isSketchSlotObject(selectedSlot)) {
      selectedSlot.isConstruction = value;
      const viewer = getViewer();
      viewer?.syncObjectBaseVisualState?.(selectedSlot);
      if (value || !viewer?.shadedSketchContoursEnabled) {
        selectedSlot.clearContourDisplay?.();
      } else {
        selectedSlot.syncContour?.();
      }
      finalizeSlotPropertyEdit(selectedSlot);
      constructionChecked.value = value;
      syncSlotPanelState();
      syncConstructionCheckedState(selectedSlot);
      return;
    }
    // 槽口是线段组合，遍历所有线段设置构造线状态。
    if (selectedSlot && isSketchCollectionObject(selectedSlot)) {
      const viewer = getViewer();
      for (const line of resolveCollectionLines(selectedSlot)) {
        line.isConstruction = value;
        viewer?.syncObjectBaseVisualState?.(line);
      }
      if (value || !viewer?.shadedSketchContoursEnabled) {
        selectedSlot.clearContourDisplay?.();
      } else {
        selectedSlot.syncContour?.();
      }
      finalizeSlotPropertyEdit(selectedSlot);
      constructionChecked.value = value;
      syncSlotPanelState();
      syncConstructionCheckedState(selectedSlot);
      return;
    }
  }
  constructionChecked.value = value;
  updateOptions({ forConstruction: value });
}

function handleCenterInput(axis: 'x' | 'y', value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  //------ 统一单位转换 开始------
  // 用户输入的毫米（mm）转换为底层所需的米（m）
  updateSelectedSlot({ type: 'center', axis, value: convertMmToM(value) });
  //------ 统一单位转换 结束------
}

function handleDisplayLengthInput(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  //------ 统一单位转换 开始------
  // 用户输入的毫米（mm）转换为底层所需的米（m）
  updateSelectedSlot({ type: 'displayLength', value: convertMmToM(value) });
  //------ 统一单位转换 结束------
}

function handleWidthInput(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  //------ 统一单位转换 开始------
  // 用户输入的毫米（mm）转换为底层所需的米（m）
  updateSelectedSlot({ type: 'width', value: convertMmToM(value) });
  //------ 统一单位转换 结束------
}

function handleRadiusInput(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  //------ 统一单位转换 开始------
  // 用户输入的毫米（mm）转换为底层所需的米（m）
  updateSelectedSlot({ type: 'radius', value: convertMmToM(value) });
  //------ 统一单位转换 结束------
}

function handleAngleInput(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  updateSelectedSlot({ type: 'angle', value });
}

function normalizeSlotAngleDegrees(value: number): number {
  let normalized = value % 360;
  if (normalized < 0) {
    normalized += 360;
  }
  return normalized;
}

function updateSelectedSlot(update: SlotParameterUpdate): void {
  const renderer = getViewer();
  const selectedSlot = getSelectedSlot();
  const selectedId = selectedSlot?.uuid ?? null;
  if (!renderer || !selectedId) {
    return;
  }
  const target = renderer.getSceneObjectById(selectedId);
  if (!target || !isSketchSlotObject(target)) {
    return;
  }

  const geometryData = target.geometryData as any;
  if (!geometryData) {
    return;
  }

  const mode = (geometryData.mode as SlotOptions['mode'] | undefined) ?? localSlotOptions.value.mode;
  const lengthType = renderer.resolveSlotLengthType(mode, localSlotOptions.value.lengthType);
  const originArray = geometryData.planeOrigin as [number, number, number] | undefined;
  const origin = originArray ? new THREE.Vector3(...originArray) : new THREE.Vector3(0, 0, 0);
  const xAxis = new THREE.Vector3(...(geometryData.xAxis ?? [1, 0, 0])).normalize();
  const yAxis = new THREE.Vector3(...(geometryData.yAxis ?? [0, 1, 0])).normalize();
  const toLocal = (point: THREE.Vector3): THREE.Vector2 => {
    const relative = point.clone().sub(origin);
    return new THREE.Vector2(relative.dot(xAxis), relative.dot(yAxis));
  };
  const toWorld = (point: THREE.Vector2): THREE.Vector3 => origin.clone().addScaledVector(xAxis, point.x).addScaledVector(yAxis, point.y);
  const round = (value: number): number => roundToEightDecimalPlaces(value);
  const widthMin = 1e-4;

  if (mode === SketchSlotCommand.Straight || mode === SketchSlotCommand.CenterStraight) {
    const startArray = geometryData.centerlineStart as [number, number, number] | undefined;
    const endArray = geometryData.centerlineEnd as [number, number, number] | undefined;
    if (!startArray || !endArray) {
      return;
    }

    let startLocal = toLocal(new THREE.Vector3(...startArray));
    let endLocal = toLocal(new THREE.Vector3(...endArray));
    let centerLocal = startLocal.clone().add(endLocal).multiplyScalar(0.5);
    let axis = endLocal.clone().sub(startLocal);
    if (axis.length() < 1e-6) {
      return;
    }
    let axisDir = axis.clone().normalize();
    let centerLength = axis.length();
    let width = Number.isFinite(geometryData.width) ? Math.max(geometryData.width as number, widthMin) : widthMin;

    if (update.type === 'center') {
      if (update.axis === 'x') {
        centerLocal.x = update.value;
      } else {
        centerLocal.y = update.value;
      }
      startLocal = centerLocal.clone().sub(axisDir.clone().multiplyScalar(centerLength / 2));
      endLocal = centerLocal.clone().add(axisDir.clone().multiplyScalar(centerLength / 2));
    } else if (update.type === 'displayLength') {
      let nextCenterLength = update.value;
      if (lengthType === SlotLengthType.Overall) {
        nextCenterLength = update.value - width;
      }
      if (!Number.isFinite(nextCenterLength) || nextCenterLength <= 1e-6) {
        return;
      }
      centerLength = nextCenterLength;
      startLocal = centerLocal.clone().sub(axisDir.clone().multiplyScalar(centerLength / 2));
      endLocal = centerLocal.clone().add(axisDir.clone().multiplyScalar(centerLength / 2));
    } else if (update.type === 'width') {
      if (!Number.isFinite(update.value) || update.value <= 0) {
        return;
      }
      width = Math.max(update.value, widthMin);
    } else if (update.type === 'angle') {
      if (!Number.isFinite(update.value)) {
        return;
      }
      const angleRad = THREE.MathUtils.degToRad(normalizeSlotAngleDegrees(update.value));
      axisDir = new THREE.Vector2(Math.cos(angleRad), Math.sin(angleRad));
      startLocal = centerLocal.clone().sub(axisDir.clone().multiplyScalar(centerLength / 2));
      endLocal = centerLocal.clone().add(axisDir.clone().multiplyScalar(centerLength / 2));
    } else {
      return;
    }

    axis = endLocal.clone().sub(startLocal);
    centerLength = axis.length();
    if (!Number.isFinite(centerLength) || centerLength <= 1e-6) {
      return;
    }
    axisDir = axis.clone().normalize();
    centerLocal = startLocal.clone().add(endLocal).multiplyScalar(0.5);

    //------ 直槽口参数更新联动 开始------
    const halfWidth = width / 2;
    const perpendicular = new THREE.Vector2(-axisDir.y, axisDir.x);
    // 计算新的接合点位置（局部 2D → 世界坐标）。
    const p1Local = startLocal.clone().add(perpendicular.clone().multiplyScalar(halfWidth));
    const p2Local = endLocal.clone().add(perpendicular.clone().multiplyScalar(halfWidth));
    const p3Local = endLocal.clone().add(perpendicular.clone().multiplyScalar(-halfWidth));
    const p4Local = startLocal.clone().add(perpendicular.clone().multiplyScalar(-halfWidth));

    const p1World = toWorld(p1Local);
    const p2World = toWorld(p2Local);
    const p3World = toWorld(p3Local);
    const p4World = toWorld(p4Local);
    const startWorld = toWorld(startLocal);
    const endWorld = toWorld(endLocal);
    const centerWorld = toWorld(centerLocal);
    const theta = Math.atan2(axisDir.y, axisDir.x);

    // 调用 SketchSlotObject 的参数更新方法，同步所有边、控制点和构造中心线。
    target.updateStraightSlotGeometry({
      p1World, p2World, p3World, p4World,
      startCenterWorld: startWorld,
      endCenterWorld: endWorld,
      centerWorld,
      halfWidth,
      theta,
    });
    //------ 直槽口参数更新联动 结束------
    const overallLength = centerLength + width;
    const displayLength = lengthType === SlotLengthType.Overall ? overallLength : centerLength;
    const angle = normalizeSlotAngleDegrees(THREE.MathUtils.radToDeg(Math.atan2(axisDir.y, axisDir.x)));

    localSlotParameters.value = {
      mode,
      center: centerWorld.toArray() as [number, number, number],
      center2d: [round(centerLocal.x), round(centerLocal.y)],
      start: startWorld.toArray() as [number, number, number],
      start2d: [round(startLocal.x), round(startLocal.y)],
      end: endWorld.toArray() as [number, number, number],
      end2d: [round(endLocal.x), round(endLocal.y)],
      width: round(width),
      centerLength: round(centerLength),
      overallLength: round(overallLength),
      displayLength: round(displayLength),
      angle: round(angle),
    };

    renderer?.setSlotDrawingParameters?.(localSlotParameters.value);
    finalizeSlotPropertyEdit(target);
    syncSlotParametersFromSelectedSlot(target);
    if (localSlotParameters.value) {
      renderer?.setSlotDrawingParameters?.(localSlotParameters.value);
    }

    return;
  }

  const centerArray = geometryData.center as [number, number, number] | undefined;
  if (!centerArray) {
    return;
  }

  const centerLocal = toLocal(new THREE.Vector3(...centerArray));
  let radius = Number.isFinite(geometryData.radius) ? (geometryData.radius as number) : 0;
  if (!Number.isFinite(radius) || radius <= 1e-6) {
    return;
  }

  const startArray = geometryData.centerlineStart as [number, number, number] | undefined;
  const endArray = geometryData.centerlineEnd as [number, number, number] | undefined;
  let startAngle = Number.isFinite(geometryData.startAngle) ? (geometryData.startAngle as number) : NaN;
  let endAngle = Number.isFinite(geometryData.endAngle) ? (geometryData.endAngle as number) : NaN;
  if ((!Number.isFinite(startAngle) || !Number.isFinite(endAngle)) && startArray && endArray) {
    const startLocal = toLocal(new THREE.Vector3(...startArray));
    const endLocal = toLocal(new THREE.Vector3(...endArray));
    startAngle = Math.atan2(startLocal.y - centerLocal.y, startLocal.x - centerLocal.x);
    endAngle = Math.atan2(endLocal.y - centerLocal.y, endLocal.x - centerLocal.x);
  }
  if (!Number.isFinite(startAngle) || !Number.isFinite(endAngle)) {
    return;
  }

  const clockwise = Boolean(geometryData.clockwise);
  let normalized = renderer.normalizeArcAngles(startAngle, endAngle, clockwise);
  startAngle = normalized.start;
  endAngle = normalized.end;
  let angleRange = normalized.angleRange;
  let width = Number.isFinite(geometryData.width) ? Math.max(geometryData.width as number, widthMin) : widthMin;

  if (update.type === 'center') {
    if (update.axis === 'x') {
      centerLocal.x = update.value;
    } else {
      centerLocal.y = update.value;
    }
  } else if (update.type === 'radius') {
    if (!Number.isFinite(update.value) || update.value <= 1e-6) {
      return;
    }
    radius = update.value;
  } else if (update.type === 'width') {
    if (!Number.isFinite(update.value) || update.value <= 0) {
      return;
    }
    width = Math.max(update.value, widthMin);
  } else if (update.type === 'angle') {
    if (!Number.isFinite(update.value) || update.value <= 0) {
      return;
    }
    angleRange = THREE.MathUtils.degToRad(update.value);
  } else if (update.type === 'displayLength') {
    let nextCenterLength = update.value;
    if (lengthType === SlotLengthType.Overall) {
      nextCenterLength = update.value - width;
    }
    if (!Number.isFinite(nextCenterLength) || nextCenterLength <= 1e-6) {
      return;
    }
    angleRange = nextCenterLength / radius;
  } else {
    return;
  }

  if (!Number.isFinite(angleRange) || angleRange <= 1e-6) {
    return;
  }

  endAngle = clockwise ? startAngle - angleRange : startAngle + angleRange;
  normalized = renderer.normalizeArcAngles(startAngle, endAngle, clockwise);
  startAngle = normalized.start;
  endAngle = normalized.end;
  angleRange = normalized.angleRange;

  const centerWorld = toWorld(centerLocal);
  const startLocal = centerLocal.clone().add(new THREE.Vector2(Math.cos(startAngle), Math.sin(startAngle)).multiplyScalar(radius));
  const endLocal = centerLocal.clone().add(new THREE.Vector2(Math.cos(endAngle), Math.sin(endAngle)).multiplyScalar(radius));
  const startWorld = toWorld(startLocal);
  const endWorld = toWorld(endLocal);
  const centerLength = radius * angleRange;
  const overallLength = centerLength + width;
  const displayLength = lengthType === SlotLengthType.Overall ? overallLength : centerLength;
  const sweepDegrees = THREE.MathUtils.radToDeg(angleRange);

  //------ 圆弧槽口参数更新联动 开始------
  /**
   * 与直槽口同路：改参数 → 调槽口几何更新入口 → 几何变化 → 参数记录随之现算。
   * 此前这里只改记录不动几何，槽口形状与面板数字当场分叉。
   */
  const halfWidth = width / 2;
  const onArc = (angle: number, arcRadius: number): THREE.Vector3 => toWorld(
    centerLocal.clone().add(new THREE.Vector2(Math.cos(angle), Math.sin(angle)).multiplyScalar(arcRadius)),
  );
  const midAngle = clockwise ? startAngle - angleRange / 2 : startAngle + angleRange / 2;

  target.updateArcSlotGeometry({
    centerWorld,
    radius,
    halfWidth,
    startAngle,
    endAngle,
    clockwise,
    // 外弧 P1→P2、内弧 P3→P4（与 updateArcSlotGeometry 的拓扑约定一致）。
    p1World: onArc(startAngle, radius + halfWidth),
    p2World: onArc(endAngle, radius + halfWidth),
    p3World: onArc(endAngle, radius - halfWidth),
    p4World: onArc(startAngle, radius - halfWidth),
    startCapCenterWorld: startWorld,
    endCapCenterWorld: endWorld,
    centerArcMidpointWorld: onArc(midAngle, radius),
  });
  //------ 圆弧槽口参数更新联动 结束------

  localSlotParameters.value = {
    mode,
    center: centerWorld.toArray() as [number, number, number],
    center2d: [round(centerLocal.x), round(centerLocal.y)],
    start: startWorld.toArray() as [number, number, number],
    start2d: [round(startLocal.x), round(startLocal.y)],
    end: endWorld.toArray() as [number, number, number],
    end2d: [round(endLocal.x), round(endLocal.y)],
    width: round(width),
    centerLength: round(centerLength),
    overallLength: round(overallLength),
    displayLength: round(displayLength),
    radius: round(radius),
    angle: round(sweepDegrees),
  };

  renderer?.setSlotDrawingParameters?.(localSlotParameters.value);
  finalizeSlotPropertyEdit(target);
  syncSlotParametersFromSelectedSlot(target);
  if (localSlotParameters.value) {
    renderer?.setSlotDrawingParameters?.(localSlotParameters.value);
  }

}
//------ 槽口属性迁移 结束------
</script>

<style scoped>
.slot-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.slot-type-grid {
  display: grid;
  grid-template-columns: repeat(2, 48px);
  gap: 8px;
}

.slot-type-button {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
}

.slot-type-button :deep(.puppy-svg-icon) {
  width: 28px;
  height: 28px;
}

.slot-type-button.active {
  border-color: rgba(66, 153, 225, 0.8);
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.4) inset;
}

.slot-type-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.length-type-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.length-type-button {
  height: 28px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.04);
  color: inherit;
  cursor: pointer;
}

.length-type-button.active {
  border-color: rgba(66, 153, 225, 0.8);
  box-shadow: inset 0 0 0 1px rgba(66, 153, 225, 0.35);
}

.length-type-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 8px;
  align-items: center;
}

.param-label {
  font-size: 12px;
}
</style>
